// dashboardViewHelper.js - View toggle state management en layout configuration
import { generateRecommendation, analyzeTeamPerformance } from './dashboardCalculations';

/**
 * View types for TeamDashboard
 */
export const VIEW_TYPES = {
  PYRAMID: 'pyramid',
  SPIDER: 'spider', 
  INDIVIDUAL: 'individual'
};

/**
 * Create view toggle configuration
 * @returns {Array} Array of view toggle button configs
 */
export const createViewToggleConfig = () => [
  {
    type: VIEW_TYPES.PYRAMID,
    label: '🔺 Piramide',
    icon: '🔺',
    title: 'Lencioni Piramide View'
  },
  {
    type: VIEW_TYPES.SPIDER,
    label: '🕸️ Spinnenweb', 
    icon: '🕸️',
    title: 'Spider Chart View'
  },
  {
    type: VIEW_TYPES.INDIVIDUAL,
    label: '👤 Individueel',
    icon: '👤', 
    title: 'Individual Scores View'
  }
];

/**
 * Get action buttons configuration - NIEUWE FUNCTIE
 * @param {Object} stats - Team completion statistics
 * @returns {Object} Action buttons configuration
 */
export const getActionButtonsConfig = (stats) => {
  // Validate stats object
  if (!stats || typeof stats.totalCompleted !== 'number' || typeof stats.totalPending !== 'number') {
    return {
      showExportButtons: false
    };
  }

  // Check if all team members completed the scan
  const allCompleted = stats.totalCompleted > 0 && stats.totalPending === 0;
  
  return {
    showExportButtons: allCompleted
  };
};

/**
 * Get layout configuration based on team data state - NIEUWE FUNCTIE
 * @param {Object} teamData - Processed team data from teamDataProcessor
 * @returns {Object} Layout configuration object
 */
export const getLayoutConfiguration = (teamData) => {
  // Robuuste check - helper heeft al alle validatie gedaan
  const hasValidData = teamData?.hasData === true;
  
  return {
    showStatsCards: true,              // Altijd zichtbaar - informatief ook bij 0 data
    showProgressBar: true,             // Altijd zichtbaar - toont 0% progress
    showToggleButtons: hasValidData,   // Alleen met data - niks te togglen zonder
    showExportDropdown: hasValidData,  // Alleen met data - niks te exporteren zonder
    showActionButtons: true,           // Altijd zichtbaar - navigation altijd nodig
    showResendButton: true,            // Altijd zichtbaar - altijd relevant
    layoutMode: hasValidData ? 'full' : 'empty'
  };
};

/**
 * Determine which content area to show - NIEUWE FUNCTIE
 * @param {Object} layoutConfig - Layout configuration from getLayoutConfiguration
 * @param {string} viewMode - Current view mode (pyramid/spider/individual)
 * @returns {Object} Content configuration
 */
export const getContentConfiguration = (layoutConfig, viewMode) => {
  if (layoutConfig.layoutMode === 'empty') {
    return {
      showEmptyState: true,
      showViewContent: false,
      contentType: 'empty'
    };
  }
  
  return {
    showEmptyState: false,
    showViewContent: true,
    contentType: viewMode
  };
};

/**
 * Get CSS classes for conditional elements - NIEUWE FUNCTIE
 * @param {Object} layoutConfig - Layout configuration
 * @returns {Object} CSS class mappings
 */
export const getConditionalClasses = (layoutConfig) => {
  return {
    toggleSection: layoutConfig.showToggleButtons 
      ? 'view-toggle-section' 
      : 'view-toggle-section view-toggle-section--hidden',
    
    exportContainer: layoutConfig.showExportDropdown
      ? 'export-dropdown-container'
      : 'export-dropdown-container export-dropdown-container--hidden',
      
    exportButton: layoutConfig.showExportDropdown
      ? 'export-btn'
      : 'export-btn export-btn--disabled'
  };
};

/**
 * Determine what content to show based on current view
 * @param {string} currentView - Current active view type
 * @param {Object} teamData - Processed team data
 * @param {Array} responses - Team responses
 * @returns {Object} Content configuration for current view
 */
export const getViewContent = (currentView, teamData, responses) => {
  const baseContent = {
    showProgress: true, // Always show progress section
    showToggleButtons: true, // Always show view toggle buttons
    showWerkvormen: currentView === VIEW_TYPES.PYRAMID, // Only in pyramid view
    showActionButtons: true // Always show footer buttons
  };

  switch (currentView) {
    case VIEW_TYPES.PYRAMID:
      return {
        ...baseContent,
        showPyramidSection: true,
        showSpiderChart: false,
        showIndividualScores: false,
        showFocusRecommendation: true,
        pyramidData: preparePyramidData(teamData),
        recommendation: generateRecommendation(teamData.focusTrede, teamData.tredeNames, teamData.scores)
      };
      
    case VIEW_TYPES.SPIDER:
      return {
        ...baseContent,
        showPyramidSection: false,
        showSpiderChart: true,
        showIndividualScores: false,
        showFocusRecommendation: false,
        showWerkvormen: false // No werkvormen in spider view
      };
      
    case VIEW_TYPES.INDIVIDUAL:
      return {
        ...baseContent,
        showPyramidSection: false,
        showSpiderChart: false,
        showIndividualScores: true,
        showFocusRecommendation: false,
        showWerkvormen: false // No werkvormen in individual view
      };
      
    default:
      return {
        ...baseContent,
        showPyramidSection: true,
        showSpiderChart: false,
        showIndividualScores: false
      };
  }
};

/**
 * Prepare pyramid data with styling and status
 * @param {Object} teamData - Processed team data from teamDataProcessor
 * @returns {Array} Array of pyramid items with styling
 */
export const preparePyramidData = (teamData) => {
  if (!teamData.hasData) {
    return [];
  }

  const pyramidOrder = ['resultaten', 'verantwoording', 'betrokkenheid', 'conflicten', 'vertrouwen'];
  
  return pyramidOrder.map((trede, index) => {
    const score = teamData.scores[trede] || 0;
    const tredeNumber = pyramidOrder.length - index; // 5,4,3,2,1
    const isFocusTrede = teamData.focusTrede === trede;
    
    // Determine styling based on score
    let status, className, badgeText, badgeIcon;
    
    if (score >= 4.0) {
      status = 'strong';
      className = 'pyramid-item--strong';
      badgeText = 'Sterk';
      badgeIcon = '💪';
    } else if (score >= 3.5) {
      status = 'sufficient';
      className = 'pyramid-item--sufficient';
      badgeText = 'Voldoende';
      badgeIcon = '👍';
    } else {
      status = 'attention';
      className = 'pyramid-item--attention';
      badgeText = 'Aandacht';
      badgeIcon = '⚠️';
    }
    
    return {
      trede,
      tredeNumber,
      tredeName: teamData.tredeNames[trede],
      score,
      isFocusTrede,
      status,
      className,
      badgeText,
      badgeIcon,
      focusMessage: isFocusTrede ? 'Focus punt - Start hier!' : null
    };
  });
};

/**
 * Get CSS classes for view toggle button
 * @param {string} viewType - The view type for this button
 * @param {string} currentView - Currently active view
 * @returns {string} CSS classes for the button
 */
export const getToggleButtonClasses = (viewType, currentView) => {
  const baseClasses = 'view-toggle-btn';
  const typeClass = ` view-toggle-btn--${viewType}`;
  const activeClass = viewType === currentView ? ' view-toggle-btn--active' : ' view-toggle-btn--inactive';
  return baseClasses + typeClass + activeClass;
};

/**
 * Handle view toggle click
 * @param {string} newView - The view to switch to
 * @param {Function} setCurrentView - State setter for current view
 * @param {Function} onViewChange - Optional callback for view change
 */
export const handleViewToggle = (newView, setCurrentView, onViewChange) => {
  setCurrentView(newView);
  
  if (onViewChange && typeof onViewChange === 'function') {
    onViewChange(newView);
  }
  
  // Optional: Track view changes for analytics
  console.log(`🔄 View changed to: ${newView}`);
};

/**
 * Get initial view based on team data
 * @param {Object} teamData - Processed team data
 * @returns {string} Initial view type to show
 */
export const getInitialView = (teamData) => {
  // Always start with pyramid view as default
  return VIEW_TYPES.PYRAMID;
};

/**
 * Check if view has required data
 * @param {string} viewType - View type to check
 * @param {Object} teamData - Team data
 * @param {Array} responses - Response data
 * @returns {boolean} Whether view can be displayed
 */
export const canShowView = (viewType, teamData, responses) => {
  if (!teamData.hasData || responses.length === 0) {
    return false;
  }
  
  switch (viewType) {
    case VIEW_TYPES.PYRAMID:
      return Object.keys(teamData.scores).length > 0;
    case VIEW_TYPES.SPIDER:
      return Object.keys(teamData.scores).length >= 3; // Need at least 3 scores for meaningful chart
    case VIEW_TYPES.INDIVIDUAL:
      return responses.length > 0;
    default:
      return false;
  }
};